import { Autocomplete, Grid } from "@mui/material";
import { makeStyles } from "@mui/styles";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import MDEditor from "components/MDEditor";
import MDTypography from "components/MDTypography";
import { useAppContextController } from "context/AppContext";
import useDropdowns from "hooks/useDropdowns";
import FormField from "layouts/applications/wizard/components/FormField";
import SelectHiringManager from "layouts/pages/events/components/InterviewEditor/SelectHiringManager";
import SelectInterviewLocation from "layouts/pages/events/components/InterviewEditor/SelectInterviewLocation";
import isEmpty from "lodash/isEmpty";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { Controller } from "react-hook-form";
import { useQueryClient } from "react-query";
import { INTERVIEW_ENTITIES } from "utils/constants/entityOptions";

const useStyle = makeStyles({
  editor: {
    height: 150,
  },
  box: {
    overflow: "visible",
    height: 550,
  },
});

const { INTERVIEW_TYPES, STATES, INTERVIEW_STATUS } = INTERVIEW_ENTITIES;

const BasicInfoStep = ({
  currentCustomer,
  clearErrors,
  setValue,
  control,
  getValues,
  errors,
  trigger,
  watch,
}) => {
  const { company, currentLoggedUser } = useAppContextController();
  const queryClient = useQueryClient();
  const classes = useStyle();
  const [editorValue, setEditorValue] = useState(null);
  const interviewLocationListRef = useRef([]);
  const { enqueueSnackbar } = useSnackbar();
  const [interviewTypesList, setInterviewTypesList] = useState();

  const { dropdowns: interviewTypes } = useDropdowns({ entity: INTERVIEW_TYPES });

  useEffect(() => {
    interviewLocationListRef.current =
      currentCustomer?.locations &&
      currentCustomer.locations.map((location) => ({
        ...location,
        label: location.locationName,
      }));
  }, [currentCustomer]);

  useEffect(() => {
    setInterviewTypesList(
      interviewTypes?.map((interview) => {
        if (interview === "Onsite {Customer}") {
          return interview.replace("{Customer}", `${company?.name}`);
        }
        if (interview === "Onsite {Primary Company}" && currentCustomer?.name) {
          return interview.replace("{Primary Company}", `${currentCustomer?.name}`);
        }
        if (interview === "Onsite {Primary Company}") {
          return interview.replace("{Primary Company}", "Customer");
        }
        return interview;
      })
    );
  }, [currentCustomer?.name, company, interviewTypes]);

  return (
    <>
      <Grid item sm={6}>
        <Controller
          name="eventDate"
          control={control}
          defaultValue={null}
          render={({ field }) => (
            <LocalizationProvider dateAdapter={AdapterMoment}>
              <DateTimePicker
                label="Start Date/Time"
                value={!field.value ? null : field.value}
                onChange={(date) => {
                  clearErrors("eventEndTime");
                  setValue("eventDate", new Date(date), { shouldDirty: true });
                  setValue(
                    "eventEndTime",
                    new Date(new Date(getValues().eventDate).getTime() + 1 * 60 * 60 * 1000),
                    { shouldDirty: true }
                  );
                  clearErrors("eventDate");
                }}
                renderInput={(params) => <FormField {...params} />}
              />
            </LocalizationProvider>
          )}
        />
        {errors?.eventDate && (
          <MDTypography className={classes.error} color="error">
            {errors?.eventDate.message}
          </MDTypography>
        )}
      </Grid>
      <Grid item sm={6}>
        <Controller
          name="eventEndTime"
          control={control}
          defaultValue={null}
          render={({ field }) => {
            return (
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label="End Date/Time"
                  value={!field.value ? null : field.value}
                  onChange={(date) => {
                    setValue("eventEndTime", new Date(date), { shouldDirty: true });
                  }}
                  renderInput={(params) => <FormField {...params} />}
                />
              </LocalizationProvider>
            );
          }}
        />
        {errors?.eventEndTime && (
          <MDTypography className={classes.error} color="error">
            {errors?.eventEndTime.message}
          </MDTypography>
        )}
      </Grid>
      <Grid item sm={4}>
        <Controller
          name="interviewType"
          control={control}
          render={({ field }) => (
            <Autocomplete
              key={`autocomplete_interviewType_${currentCustomer?._id}`}
              options={interviewTypesList || []}
              autoSelect
              freeSolo
              name="Interview Type"
              defaultValue={null}
              value={field.value || null}
              onChange={(e, v) => {
                field.onChange(v?.charAt(0).toUpperCase() + v?.slice(1));
                setValue("interviewLocation", null);
              }}
              onBlur={() => trigger("interviewType")}
              renderInput={(params) => (
                <FormField
                  key={`interviewType_${currentCustomer?._id}`}
                  {...params}
                  type="text"
                  label="Interview Type"
                />
              )}
            />
          )}
        />
        {errors?.interviewType && (
          <MDTypography className={classes.error} color="error">
            {errors?.interviewType.message}
          </MDTypography>
        )}
      </Grid>
      <Grid item sm={4}>
        <Controller
          name="interviewLocation"
          control={control}
          render={({ field }) => (
            <SelectInterviewLocation
              value={field.value || null}
              onChange={(e, v) => {
                field.onChange(v);
              }}
              isOptionEqualToValue={(option, value) => option?.label === value?.label}
              currentEvent={null}
              company={company}
              currentCompany={currentCustomer}
              selectedCompany={currentCustomer.name}
              selectedInterviewType={watch("interviewType")}
            />
          )}
        />
      </Grid>
      <Grid item sm={4}>
        <Controller
          name="hiringManager"
          control={control}
          render={({ field, fieldState: { error } }) => (
            <SelectHiringManager
              customer={currentCustomer}
              value={field.value || ""}
              onChange={(e, v) => {
                field.onChange(v);
              }}
              onBlur={() => trigger("hiringManager")}
              errorMessage={error?.message}
              isShrink={!isEmpty(getValues("hiringManager"))}
            />
          )}
        />
      </Grid>
      <Grid item xs={12} sx={{ height: "250px" }}>
        <MDTypography component="label" variant="body2">
          Description
        </MDTypography>
        <Controller
          name="companyDescription"
          control={control}
          render={({ field }) => (
            <MDEditor
              name="description"
              className={classes.editor}
              value={editorValue || ""}
              onChange={(e) => {
                setEditorValue(e);
                setValue("description", e, { shouldDirty: true });
              }}
            />
          )}
        />
      </Grid>
    </>
  );
};

export default BasicInfoStep;
